import { useState, useCallback, useEffect, useMemo, useContext } from "react";
import { intl, LanguageBaseContext, fetchData, HTTP_STATUS } from "dmsbasereact";
import Swal from 'sweetalert2';

const MODAL_TITLES = {
    sso: { key: "CreateSsoUser", default: "Create SSO User" },
    person: { key: "CreateResponsible", default: "Create Responsible" },
    distributor_responsible: { key: "CreateDistributorResponsible", default: "Create Distributor Responsible" },
    pharmacist: { key: "CreateDispenser", default: "Create Dispenser" },
    doctor: { key: "CreatePrescriber", default: "Create Prescriber" },
    auditor: { key: "CreateAuditor", default: "Create Auditor" },
    default: { key: "CreateUser", default: "Create User" },
};

const DEFAULT_ROLES_BY_TYPE = {
    sso: [],
    person: ["INSTITUTION_RESPONSIBLE"],
    distributor_responsible: ["DISTRIBUTOR_RESPONSIBLE"],
    pharmacist: ["Pharmacist"],
    doctor: ["Doctor"],
    auditor: ["ORGAO_REGULADOR_AUDITOR"],
    default: [],
};

const HELPER_TEXT_KEYS = {
    sso: { key: "SsoUserHelperText", default: "SSO users are authenticated via Microsoft Entra ID. The email must match the corporate email." },
    person: { key: "ResponsibleHelperText", default: "Institution responsibles manage partner pharmacies and clinics." },
    distributor_responsible: { key: "DistributorResponsibleHelperText", default: "Distributor responsibles manage distributor institutions (category 100)." },
    pharmacist: { key: "DispenserHelperText", default: "Dispensers are pharmacists responsible for medication dispensing." },
    doctor: { key: "PrescriberHelperText", default: "Prescribers are doctors authorized to create prescriptions in the system." },
    auditor: { key: "AuditorHelperText", default: "Auditors have read-only access for regulatory compliance. Access can be deactivated at any time." },
    default: { key: "", default: "" },
};

const isValidCPF = (cpf) => {
    if (!cpf) return false;
    cpf = cpf.replace(/\D/g, "");
    if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(cpf.charAt(i)) * (10 - i);
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.charAt(9))) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(cpf.charAt(i)) * (11 - i);
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.charAt(10))) return false;
    return true;
};

export const useUserForm = ({
    isVisible,
    onSave,
    user,
    availableRoles,
    userType,
}) => {
    const { language: systemLanguage } = useContext(LanguageBaseContext);
    const isEditMode = Boolean(user);

    const [formName, setFormName] = useState("");
    const [formEmail, setFormEmail] = useState("");
    const [formPhone, setFormPhone] = useState("");
    const [formLanguage, setFormLanguage] = useState(systemLanguage);
    const [formStatus, setFormStatus] = useState(1);
    const [formRoles, setFormRoles] = useState([]);
    const [formPassword, setFormPassword] = useState("");
    const [formPasswordConfirm, setFormPasswordConfirm] = useState("");
    const [formCpf, setFormCpf] = useState("");
    const [cpfIsValid, setCpfIsValid] = useState(false);
    const [formCrms, setFormCrms] = useState([]);
    const [formCrf, setFormCrf] = useState("");
    const [formCrfState, setFormCrfState] = useState("");
    const [formInstitution, setFormInstitution] = useState(
        (userType === "doctor" || userType === "person" || userType === "distributor_responsible") ? [] : ""
    );
    const [formZipCode, setFormZipCode] = useState("");
    const [formAddress, setFormAddress] = useState("");
    const [formAddressNumber, setFormAddressNumber] = useState("");
    const [formCity, setFormCity] = useState("");
    const [formAddressState, setFormAddressState] = useState("");
    const [institutionModalVisible, setInstitutionModalVisible] = useState(false);
    const [createdInstitution, setCreatedInstitution] = useState(null);
    const [institutionAddress, setInstitutionAddress] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    const needsPersonFields = ["doctor", "pharmacist", "person", "distributor_responsible", "auditor"].includes(userType);
    const needsDoctorFields = userType === "doctor";
    const needsPharmacistFields = userType === "pharmacist";
    const needsInstitutionField = ["doctor", "pharmacist", "person", "distributor_responsible"].includes(userType);
    const showPasswordField = !isEditMode && userType !== "sso";
    // Address is only required for doctors and institution responsibles, not for pharmacists
    const needsAddressFields = ["doctor", "person", "distributor_responsible"].includes(userType);

    // Form validation with detailed errors
    const { isFormValid, validationErrors, fieldErrors } = useMemo(() => {
        const errors = [];

        // Email is always required (used as login)
        if (!formEmail || formEmail.trim().length === 0) {
            errors.push({ field: "email", message: intl.get("EmailRequired").d("Email is required") });
        }
        // Name is always required
        if (!formName || formName.trim().length === 0) {
            errors.push({ field: "name", message: intl.get("NameRequired").d("Name is required") });
        }
        // Phone is required
        if (!formPhone || formPhone.trim().length === 0) {
            errors.push({ field: "phone", message: intl.get("PhoneRequired").d("Phone is required") });
        }
        // Password validation (only when password field is shown and password is provided)
        if (showPasswordField && formPassword) {
            if (formPassword.length < 8) {
                errors.push({ field: "password", message: intl.get("PasswordTooShort").d("Password must be at least 8 characters long") });
            }
            if (!/[A-Z]/.test(formPassword)) {
                errors.push({ field: "password", message: intl.get("PasswordNeedsUppercase").d("Password must contain at least one uppercase letter") });
            }
            if (!/[a-z]/.test(formPassword)) {
                errors.push({ field: "password", message: intl.get("PasswordNeedsLowercase").d("Password must contain at least one lowercase letter") });
            }
            if (!/\d/.test(formPassword)) {
                errors.push({ field: "password", message: intl.get("PasswordNeedsNumber").d("Password must contain at least one number") });
            }
            if (!/[!@#$%^&*(),.?":{}|<>]/.test(formPassword)) {
                errors.push({ field: "password", message: intl.get("PasswordNeedsSpecialChar").d("Password must contain at least one special character") });
            }
            if (formPassword !== formPasswordConfirm) {
                errors.push({ field: "passwordConfirm", message: intl.get("PasswordsMustMatch").d("Passwords must match") });
            }
        }
        // CPF validation for person types
        if (needsPersonFields) {
            if (!formCpf) {
                errors.push({ field: "cpf", message: intl.get("CpfRequired").d("CPF is required") });
            } else if (!cpfIsValid) {
                errors.push({ field: "cpf", message: intl.get("ValidCpfRequired").d("Valid CPF is required") });
            }
        }
        // CRM validation for doctors
        if (needsDoctorFields) {
            if (formCrms.length === 0 || !formCrms[0]?.crm?.trim()) {
                errors.push({ field: "crm", message: intl.get("CrmRequired").d("CRM is required") });
            }
            if (formCrms.length === 0 || !formCrms[0]?.crm_state?.trim()) {
                errors.push({ field: "crmState", message: intl.get("CrmStateRequired").d("CRM state is required") });
            }
        }
        // CRF validation for pharmacists
        if (needsPharmacistFields) {
            if (!formCrf || formCrf.trim().length === 0) {
                errors.push({ field: "crf", message: intl.get("CrfRequired").d("CRF is required") });
            }
            if (!formCrfState || formCrfState.trim().length === 0) {
                errors.push({ field: "crfState", message: intl.get("CrfStateRequired").d("CRF state is required") });
            }
        }
        // Address validation - only for doctors and institution responsibles, not for pharmacists
        if (needsAddressFields) {
            if (!formZipCode || formZipCode.trim().length === 0) {
                errors.push({ field: "zipCode", message: intl.get("ZipCodeRequired").d("Zip code is required") });
            }
            if (!formAddress || formAddress.trim().length === 0) {
                errors.push({ field: "address", message: intl.get("AddressRequired").d("Address is required") });
            }
            if (!formAddressNumber || formAddressNumber.trim().length === 0) {
                errors.push({ field: "addressNumber", message: intl.get("NumberRequired").d("Number is required") });
            }
            if (!formCity || formCity.trim().length === 0) {
                errors.push({ field: "city", message: intl.get("CityRequired").d("City is required") });
            }
            if (!formAddressState) {
                errors.push({ field: "addressState", message: intl.get("StateRequired").d("State is required") });
            }
        }

        // Convert errors array to field map
        const errorMap = errors.reduce((acc, err) => {
            acc[err.field] = err.message;
            return acc;
        }, {});

        // Only show field errors after user attempts to submit
        const visibleFieldErrors = submitted ? errorMap : {};

        return {
            isFormValid: errors.length === 0,
            validationErrors: errors,
            fieldErrors: visibleFieldErrors,
        };
    }, [formEmail, formName, formPhone, formPassword, formPasswordConfirm, showPasswordField, formCpf, cpfIsValid, formCrms, formCrf, formCrfState, needsPersonFields, needsDoctorFields, needsPharmacistFields, needsAddressFields, formZipCode, formAddress, formAddressNumber, formCity, formAddressState, submitted]);

    const getDefaultRoles = useCallback((type) => {
        if (!availableRoles || availableRoles.length === 0) return [];
        const defaultRoleNames = DEFAULT_ROLES_BY_TYPE[type] || DEFAULT_ROLES_BY_TYPE.default;
        return availableRoles
            .filter(role => defaultRoleNames.includes(role.role_name))
            .map(role => role.role_name);
    }, [availableRoles]);

    useEffect(() => {
        if (isVisible && user) {
            setFormName(user.user_name || "");
            setFormEmail(user.user_email || "");
            setFormPhone(user.user_phone || "");
            setFormLanguage(user.user_language || systemLanguage);
            setFormStatus(user.status ?? 1);
            setFormRoles(user.roles || []);
            const userCpf = user.person?.cpf || "";
            setFormCpf(userCpf);
            setCpfIsValid(isValidCPF(userCpf));
            const doctorCrms = [];
            if (user.doctor?.crm && user.doctor?.crm_state) {
                doctorCrms.push({ crm: user.doctor.crm, crm_state: user.doctor.crm_state });
            }
            if (user.doctor?.additional_crms) {
                doctorCrms.push(...user.doctor.additional_crms);
            }
            setFormCrms(doctorCrms);
            setFormCrf(user.pharmacist?.crf || "");
            setFormCrfState(user.pharmacist?.crf_state || "");
            // Address fields
            setFormZipCode(user.address?.zip_code || "");
            setFormAddress(user.address?.address || "");
            setFormAddressNumber(user.address?.number || "");
            setFormCity(user.address?.city || "");
            setFormAddressState(user.address?.state || "");
            // Handle institution - for doctors it can be multiple, for others single
            if (userType === "doctor") {
                const institutionPks = user.institutions?.map(i => i.pk) || (user.institution?.pk ? [user.institution.pk] : []);
                setFormInstitution(institutionPks);
            } else {
                setFormInstitution(user.institution?.pk || "");
            }
        } else if (isVisible && !user) {
            setFormName("");
            setFormEmail("");
            setFormPhone("");
            setFormLanguage(systemLanguage);
            setFormStatus(1);
            setFormPassword("");
            setFormPasswordConfirm("");
            setFormCpf("");
            setCpfIsValid(false);
            setFormCrms([]);
            setFormCrf("");
            setFormCrfState("");
            setFormInstitution(userType === "doctor" ? [] : "");
            setFormZipCode("");
            setFormAddress("");
            setFormAddressNumber("");
            setFormCity("");
            setFormAddressState("");
            const defaultRoles = getDefaultRoles(userType);
            setFormRoles(defaultRoles);
        } else if (!isVisible) {
            setFormName("");
            setFormEmail("");
            setFormPhone("");
            setFormLanguage(systemLanguage);
            setFormStatus(1);
            setFormRoles([]);
            setFormPassword("");
            setFormPasswordConfirm("");
            setFormCpf("");
            setCpfIsValid(false);
            setFormCrms([]);
            setFormCrf("");
            setFormCrfState("");
            setFormInstitution(userType === "doctor" ? [] : "");
            setFormZipCode("");
            setFormAddress("");
            setFormAddressNumber("");
            setFormCity("");
            setFormAddressState("");
            setSubmitted(false);
        }
    }, [isVisible, user, userType, getDefaultRoles, systemLanguage]);

    useEffect(() => {
        if (isVisible && !user && availableRoles.length > 0 && formRoles.length === 0) {
            const defaultRoles = getDefaultRoles(userType);
            if (defaultRoles.length > 0) {
                setFormRoles(defaultRoles);
            }
        }
    }, [isVisible, user, availableRoles, userType, formRoles.length, getDefaultRoles]);

    const handleInstitutionSave = useCallback(async (payload) => {
        try {
            // Use authenticated endpoint for admin-created institutions
            const response = await fetchData("/institution", "POST", true, payload);
            if (response.status === HTTP_STATUS.CREATED_201 || response.status === HTTP_STATUS.OK_200) {
                const newInst = response.data.data;
                setCreatedInstitution(newInst);
                // Set institution based on user type
                if (userType === "doctor") {
                    // For doctors, add to array
                    setFormInstitution(prev => [...(Array.isArray(prev) ? prev : []), newInst.organization_branch_pk]);
                } else {
                    // For others, set single value
                    setFormInstitution(newInst.organization_branch_pk);
                }
                setInstitutionModalVisible(false);
                return true;
            } else {
                const errorMsg = response.data?.detail || intl.get("ErrorCreatingInstitution").d("Erro ao criar instituição. Tente novamente.");

                Swal.fire({
                    icon: "error",
                    title: errorMsg
                });

                return false;
            }
        } catch (error) {
            console.error("Error creating institution:", error);

            let errorMessage;

            if (error.response?.status === 409) {
                // CNPJ already exists
                errorMessage = intl.get("CnpjAlreadyExists").d("Já existe uma instituição com este CNPJ. Selecione-a na lista.");
            } else if (error.response?.data?.detail) {
                // Show backend error detail
                errorMessage = error.response.data.detail;
            } else if (error.message) {
                // Show generic error message
                errorMessage = intl.get("ErrorCreatingInstitution").d("Erro ao criar instituição") + ": " + error.message;
            } else {
                // Fallback error message
                errorMessage = intl.get("ErrorCreatingInstitution").d("Erro ao criar instituição. Tente novamente.");
            }

            Swal.fire({
                icon: "error",
                title: errorMessage
            });

            return false;
        }
    }, [userType]);
    
    // Fetch institution address for pharmacists when institution is selected
    useEffect(() => {
        const fetchInstitutionAddress = async () => {
            if (needsPharmacistFields && formInstitution && formInstitution !== "") {
                try {
                    const response = await fetchData(`/organization_branch/${formInstitution}`, 'GET', true);
                    if (response?.data?.data) {
                        setInstitutionAddress({
                            name: response.data.data.name,
                            address: response.data.data.address
                        });
                    } else {
                        setInstitutionAddress(null);
                    }
                } catch (error) {
                    console.error("Error fetching institution address:", error);
                    setInstitutionAddress(null);
                }
            } else {
                setInstitutionAddress(null);
            }
        };

        fetchInstitutionAddress();
    }, [formInstitution, needsPharmacistFields]);

    const handleSave = useCallback(() => {
        // Show errors on first submit attempt
        setSubmitted(true);

        // Don't proceed if form is invalid
        if (!isFormValid) return;

        const cleanPhone = formPhone ? formPhone.replace(/\D/g, '') : null;

        const payload = {
            user_login: formEmail,
            user_name: formName,
            user_email: formEmail || null,
            user_phone: cleanPhone,
            user_language: formLanguage,
            status: formStatus,
            roles: formRoles,
            user_type: userType || null,
        };
        if (!isEditMode && formPassword) payload.user_password = formPassword;
        if (needsPersonFields) {
            payload.cpf = formCpf || null;
        }
        // Address only for doctors and institution responsibles, not for pharmacists
        if (needsAddressFields) {
            payload.address = {
                address_name: formName,
                number: formAddressNumber,
                address: formAddress,
                zip_code: formZipCode ? formZipCode.replace(/\D/g, '') : null,
                city: formCity,
                state: formAddressState,
                country_code: "BR",
            };
        }
        if (needsDoctorFields) {
            payload.crms = formCrms;
            if (formCrms.length > 0) {
                payload.crm = formCrms[0].crm;
                payload.crm_state = formCrms[0].crm_state;
            }
        }
        if (needsPharmacistFields) {
            payload.crf = formCrf || null;
            payload.crf_state = formCrfState || null;
        }
        if (needsInstitutionField) {
            if (userType === "doctor" || userType === "person" || userType === "distributor_responsible") {
                // For doctors, institution responsibles, and distributor responsibles - send array
                const validInstitutions = Array.isArray(formInstitution)
                    ? formInstitution.filter(pk => pk !== "NO-INSTITUTION")
                    : [];
                payload.institution_pks = validInstitutions.length > 0 ? validInstitutions : null;
                // Also set institution_pk for backwards compatibility (first institution)
                payload.institution_pk = validInstitutions.length > 0 ? validInstitutions[0] : null;
            } else {
                payload.institution_pk = formInstitution && formInstitution !== "NO-INSTITUTION" ? formInstitution : null;
            }
        }
        onSave(payload, user?.pk);
    }, [
        formName, formEmail, formPhone, formLanguage,
        formStatus, formRoles, formPassword, formCpf,
        formCrms, formCrf, formCrfState, formInstitution,
        formZipCode, formAddress, formAddressNumber, formCity, formAddressState,
        isEditMode, onSave, user, userType,
        needsPersonFields, needsDoctorFields, needsPharmacistFields, needsInstitutionField, needsAddressFields,
        isFormValid
    ]);

    const title = useMemo(() => {
        if (isEditMode) return intl.get("EditUser").d("Edit User");
        const titleConfig = MODAL_TITLES[userType] || MODAL_TITLES.default;
        return intl.get(titleConfig.key).d(titleConfig.default);
    }, [isEditMode, userType]);

    const helperText = useMemo(() => {
        if (isEditMode) return "";
        const helperConfig = HELPER_TEXT_KEYS[userType] || HELPER_TEXT_KEYS.default;
        if (!helperConfig.key) return "";
        return intl.get(helperConfig.key).d(helperConfig.default);
    }, [isEditMode, userType]);

    return {
        isEditMode,
        formName, setFormName,
        formEmail, setFormEmail,
        formPhone, setFormPhone,
        formLanguage, setFormLanguage,
        formStatus, setFormStatus,
        formRoles, setFormRoles,
        formPassword, setFormPassword,
        formPasswordConfirm, setFormPasswordConfirm,
        formCpf, setFormCpf,
        setCpfIsValid,
        formCrms, setFormCrms,
        formCrf, setFormCrf,
        formCrfState, setFormCrfState,
        formInstitution, setFormInstitution,
        formZipCode, setFormZipCode,
        formAddress, setFormAddress,
        formAddressNumber, setFormAddressNumber,
        formCity, setFormCity,
        formAddressState, setFormAddressState,
        institutionAddress,
        needsPersonFields,
        needsDoctorFields,
        needsPharmacistFields,
        needsInstitutionField,
        needsAddressFields,
        showPasswordField,
        handleSave,
        title,
        helperText,
        isFormValid,
        validationErrors,
        institutionModalVisible,
        setInstitutionModalVisible,
        handleInstitutionSave,
        createdInstitution,
        fieldErrors,
    };
};
